import { useNavigate } from 'react-router-dom';
import {
  Package,
  Truck,
  ShoppingCart,
  Banknote,
  Wallet,
  BarChart3,
  Bell,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Warehouse,
  FileText,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import OliveIcon from '@/components/ui/OliveIcon';

const features = [
  { icon: Package, title: 'Productos y Categorías', description: 'ABM completo con precios, costos y fórmulas de margen' },
  { icon: Warehouse, title: 'Control de Stock', description: 'Inventario con alertas de stock bajo y vencimiento' },
  { icon: ShoppingCart, title: 'Ventas', description: 'Ventas rápidas con descuentos y múltiples métodos de pago' },
  { icon: Truck, title: 'Compras y Proveedores', description: 'Órdenes de compra y cuenta corriente con proveedores' },
  { icon: Banknote, title: 'Caja Diaria', description: 'Apertura y cierre con movimientos y arqueo' },
  { icon: Wallet, title: 'Finanzas', description: 'Gastos fijos, gastos del negocio y balance general' },
  { icon: BarChart3, title: 'Reportes', description: 'Top productos, ventas por período y rentabilidad' },
  { icon: Bell, title: 'Alertas', description: 'Stock bajo y productos por vencer al instante' },
];

const benefits = [
  'Multi-usuario con roles y permisos',
  'Funciona en cualquier dispositivo',
  'Datos seguros en la nube',
  'Soporte técnico incluido',
  'Actualizaciones automáticas',
  'Sin instalación requerida',
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-white to-cream-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-taupe-600 to-taupe-800 rounded-lg flex items-center justify-center">
                <OliveIcon className="w-6 h-6" />
              </div>
              <span className="text-xl font-bold text-taupe-800">ANIMAstore</span>
            </div>
            <div className="flex items-center gap-3">
              <a href="#features" className="hidden md:inline text-sm text-taupe-600 hover:text-taupe-800 px-3">Funcionalidades</a>
              <a href="#contact" className="hidden md:inline text-sm text-taupe-600 hover:text-taupe-800 px-3">Contacto</a>
              <Button onClick={() => navigate('/login')} className="bg-taupe-700 hover:bg-taupe-800 text-cream-50">
                Iniciar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-taupe-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cream-300/40 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-taupe-100 text-taupe-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Sistema de Gestión de Almacenes
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-taupe-900 leading-tight">
              Gestioná tu almacén
              <span className="block text-taupe-600">de punta a punta</span>
            </h1>
            <p className="mt-6 text-lg text-taupe-600 leading-relaxed">
              Inventario, ventas, caja, compras y finanzas en una sola plataforma.
              Diseñado para almacenes, distribuidoras y comercios mayoristas.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-taupe-700 hover:bg-taupe-800 text-cream-50 px-8 py-3 text-base"
              >
                Solicitar Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center justify-center font-medium rounded-lg px-8 py-3 text-base bg-white hover:bg-cream-100 text-taupe-700 border border-taupe-300 transition-colors"
              >
                Iniciar Sesión
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-taupe-900">Todo lo que tu almacén necesita</h2>
            <p className="mt-4 text-lg text-taupe-600">Funcionalidades pensadas para el día a día del negocio</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group p-6 bg-white rounded-2xl border border-cream-200 hover:border-taupe-300 hover:shadow-sm transition-all"
                >
                  <div className="w-11 h-11 rounded-xl border border-taupe-200 bg-cream-50 flex items-center justify-center mb-4 group-hover:bg-taupe-700 group-hover:border-taupe-700 transition-colors">
                    <Icon className="w-5 h-5 text-taupe-600 group-hover:text-cream-50 transition-colors" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-semibold text-taupe-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-taupe-600 leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-taupe-700 to-taupe-900 text-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Por qué elegir ANIMAstore</h2>
              <p className="text-cream-200 text-lg leading-relaxed mb-8">
                Dejá las planillas de Excel y los cuadernos. Tené todo tu negocio
                organizado, accesible y respaldado en un solo lugar.
              </p>
              <ul className="space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-cream-300 flex-shrink-0" />
                    <span className="text-cream-100">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-cream-400/10 backdrop-blur-sm rounded-2xl p-6">
                <Package className="w-8 h-8 text-cream-300 mb-3" />
                <p className="text-2xl font-bold">+1.000</p>
                <p className="text-cream-300 text-sm">Productos por almacén</p>
              </div>
              <div className="bg-cream-400/10 backdrop-blur-sm rounded-2xl p-6">
                <ShoppingCart className="w-8 h-8 text-cream-300 mb-3" />
                <p className="text-2xl font-bold">Ventas</p>
                <p className="text-cream-300 text-sm">Rápidas y con stock</p>
              </div>
              <div className="bg-cream-400/10 backdrop-blur-sm rounded-2xl p-6">
                <FileText className="w-8 h-8 text-cream-300 mb-3" />
                <p className="text-2xl font-bold">Reportes</p>
                <p className="text-cream-300 text-sm">En tiempo real</p>
              </div>
              <div className="bg-cream-400/10 backdrop-blur-sm rounded-2xl p-6">
                <Wallet className="w-8 h-8 text-cream-300 mb-3" />
                <p className="text-2xl font-bold">Finanzas</p>
                <p className="text-cream-300 text-sm">Caja y gastos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-cream-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-taupe-900">Probá ANIMAstore en tu almacén</h2>
          <p className="mt-4 text-lg text-taupe-600">
            Escribinos por WhatsApp y coordinamos una demo personalizada de tu negocio.
          </p>

          {/* CTA WhatsApp directo */}
          <a
            href="https://wa.me/5493815791046?text=Hola!%20Me%20interesa%20ANIMAstore%20para%20mi%20negocio."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 mt-8 px-8 py-4 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-2xl font-semibold text-base shadow-sm hover:shadow-md transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Contactanos por WhatsApp
          </a>

          <p className="mt-4 text-sm text-taupe-400">Te respondemos en el día</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-taupe-900 text-cream-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-taupe-600 to-taupe-800 rounded-lg flex items-center justify-center">
              <OliveIcon className="w-5 h-5" />
            </div>
            <span className="font-bold text-cream-100">ANIMAstore</span>
          </div>
          <p className="text-sm text-cream-400">© 2025 ANIMAstore. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
